import React, {ReactElement} from 'react';
import StyledRangeSlider, {
  StyledBar,
  StyledBarChart,
  StyledLeftThumb,
  StyledNumbers,
  StyledRange,
  StyledRightThumb,
  StyledSlider,
  StyledTrack,
} from './styles';
import Props from './typings';

const RangeSlider = ({
  min = 0,
  max,
  minVal = min,
  maxVal = max,
  step = 1,
  thumbGap = 0,
  rangeBars,
  onChange,
  onMouseUp,
  prefix,
  suffix,
  infinite = false,
}: Props): ReactElement => {
  const minValueChange = (e) => {
    const value = Math.min(+e.target.value, maxVal - thumbGap);
    onChange({min: value === min ? null : value, max: maxVal === max ? null : maxVal});
  };

  const maxValueChange = (e) => {
    const value = Math.max(+e.target.value, minVal + thumbGap);
    onChange({min: minVal === min ? null : minVal, max: value === max ? null : value});
  };

  const getPercent = (value) => Math.round(((value - min) / (max - min)) * 100);

  const getHeighestCount = () => {
    if (!rangeBars) return 0;
    return Math.max(...rangeBars.map((bar) => bar.items_count));
  };

  const getBarWidth = (bar) => {
    if (bar.to_price) {
      return ((bar.to_price - bar.from_price) * 100) / max;
    } else {
      return ((max - bar.from_price) * 100) / max;
    }
  };

  return (
    <StyledRangeSlider>
      {rangeBars && (
        <StyledBarChart>
          {rangeBars.map((bar, key) => (
            <StyledBar
              $barHeight={Math.round((bar.items_count * 100) / getHeighestCount())}
              $active={
                (minVal > min || maxVal < max) &&
                maxVal >= bar.from_price &&
                (!bar.to_price || minVal <= bar.to_price)
              }
              $barWidth={Math.round(getBarWidth(bar))}
              key={key}
            />
          ))}
        </StyledBarChart>
      )}

      <StyledRightThumb
        type="range"
        min={min}
        max={max}
        value={minVal}
        step={step}
        onChange={minValueChange}
        onMouseUp={onMouseUp}
        onTouchEnd={onMouseUp}
      />

      <StyledLeftThumb
        type="range"
        min={min}
        max={max}
        value={maxVal}
        step={step}
        onChange={maxValueChange}
        onMouseUp={onMouseUp}
        onTouchEnd={onMouseUp}
      />

      <StyledSlider>
        <StyledTrack />

        <StyledRange
          $rangeWidth={getPercent(maxVal) - getPercent(minVal)}
          $rangeLeftMargin={getPercent(minVal)}
        ></StyledRange>
      </StyledSlider>

      <StyledNumbers>
        <span>
          {prefix}
          {minVal}
          {suffix}
        </span>

        <span>
          {prefix}
          {maxVal}
          {suffix}
          {infinite && maxVal === max && '+'}
        </span>
      </StyledNumbers>
    </StyledRangeSlider>
  );
};

export default RangeSlider;
